import React, { Component } from 'react'
import { connect } from 'react-redux'

import '../../incidentList.scss'
import LoaderWrapper from '../../common/LoaderWrapper/LoaderWrapper'
import Loading from '../../common/LoaderWrapper/loading'
import Paginator from '../../common/paginator/paginator'
import Tooltip from '../../common/popover/tooltip'
import DetailsPopover from '../../common/popover/detailsPopover'
import ThreatFeedStatusMenu from './ThreatFeedStatusMenu'
import { formatThreatFeedDatetime } from '../../../utilities/datetime'
import { getStatusClass } from '../../../utilities/statusCSSClass'
import { getOSType } from '../../../utilities/OSTypes'
import {
  loadNextEvents,
  loadPrevEvents,
  loadEvents,
  setThreatFiltersInStore,
} from '../../../store/entities/events'
import { isGlobalView } from '../../../utilities/config'

// ===========================================================================
// Threat Feed — list view (PIXMC-6500).
//
// Custom table markup ported from the claude-dashboards mockup (the client asked for this
// list to match the mockup, not reuse IncidentList). Data, pagination and the Update Status
// dropdown stay on the shared events slice — this is a presentation-only, isolated UI.
// Renders inside ThreatFeed's card (no own card wrapper).
// ===========================================================================

// Status badge colour reuses the shared getStatusClass → Common.scss classes (verifyText /
// pixmVerifiedText / reportedPhishText / unverifyText), identical to the legacy Incidents list.
const userName = (e) => e.osUserId || e.osComputerName || e.osuuid || e.email || 'NA'

// The User column reuses the legacy DetailsPopover with the exact same payload the legacy
// IncidentList feeds it, so the hover popup is identical.
const DeviceType = new Map([['D', 'Desktop'], ['M', 'Mobile']])
const userDetails = (e) => ({
  User: userName(e),
  'User ID': e.osUserId || 'NA',
  Platform: `${getOSType(e.os)}, ${e.browserName}`,
  'Machine ID': e.osuuid || 'NA',
  'Computer Name': e.osComputerName || 'NA',
  Device: e.deviceType ? DeviceType.get(e.deviceType) : 'NA',
})

class ThreatFeedTable extends Component {
  // Opened is the only sortable column (client decision); flips the `order` param the
  // all-events API already honours (ORDER BY DetectionTime asc/desc) and reloads.
  toggleSort = () => {
    const { threatFilters, selectedCustomer } = this.props
    if (!selectedCustomer) return
    const order = threatFilters.order === 'asc' ? 'desc' : 'asc'
    const next = { ...threatFilters, order, offset: 0 }
    this.props.setThreatFiltersInStore(next)
    this.props.loadEvents(
      next,
      selectedCustomer.id,
      selectedCustomer.defaultCustomerId,
      isGlobalView(selectedCustomer.organizationName),
    )
  }

  render() {
    const { events, threatFilters, totalSize, loading, isLastPage, onDetails } = this.props
    const { offset } = threatFilters
    const rows = events.slice(offset, offset + 20)
    const asc = threatFilters.order === 'asc'
    // Remount the Paginator when any filter (except offset) changes so its internally-cached
    // filters stay fresh — otherwise paging re-sends the previous startTime (stale filters).
    const pagerKey = [
      threatFilters.startTime, threatFilters.endTime, threatFilters.order,
      threatFilters.brand || '', threatFilters.extensionUserId || '',
      threatFilters.triagedEvent != null ? threatFilters.triagedEvent : '',
      threatFilters.pixmVerified != null ? threatFilters.pixmVerified : '',
      (threatFilters.type || []).join(','),
    ].join('|')

    return (
      <>
        <div className="tfResultsCount">
          Results ({rows.length === 0 ? 0 : isLastPage ? offset + rows.length : `More than ${offset + rows.length}`})
        </div>
        <div className="tfCardBody">
          {/* Loader overlays the table only (existing Loading component); content stays
              mounted underneath during filter / sort / page loads. */}
          {loading && <div className="tfLoaderOverlay"><Loading loading={true} /></div>}
          <LoaderWrapper
            loading={false}
            data={rows}
            styles={{ minHeight: '400px' }}
            placeholderText={'No Threats Detected'}
          >
            <>
              <div className="tfTableWrap">
                <table className="listTable">
                  <thead>
                    <tr>
                      <th className="tableHead">User</th>
                      <th className="tableHead">Brand</th>
                      <th className="tableHead">URL</th>
                      <th className="tableHead tfSortable" onClick={this.toggleSort}>
                        Opened
                        <svg
                          className={`tfSortCaret ${asc ? 'tfSortAsc' : ''}`}
                          viewBox="0 0 10 6"
                          aria-hidden="true"
                        >
                          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </th>
                      <th className="tableHead">Action</th>
                      <th className="tableHead">Status</th>
                      <th className="tableHead" aria-label="Row actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>
                          <DetailsPopover header={userName(item)} data={userDetails(item)} />
                        </td>
                        <td><span className="tfBrandText">{item.brands || 'NA'}</span></td>
                        <td>
                          <div className="tfUrl" data-tip data-for={item.url}>{item.url}</div>
                          <Tooltip id={item.url} data={item.url} className="tfTooltipWrap" />
                        </td>
                        <td className="tfOpenedCell">{formatThreatFeedDatetime(item.detectionTime)}</td>
                        <td>{item.userAction}</td>
                        <td><span className={getStatusClass(item.status)}><b>{item.status}</b></span></td>
                        <td>
                          <div className="tfActionsCell">
                            <ThreatFeedStatusMenu event={item} onAction={this.props.onStatusAction} />
                            <button className="tfBtn" onClick={() => onDetails(item)}>View Details</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="tfPager">
                <Paginator
                  key={pagerKey}
                  onNext={this.props.loadNextEvents}
                  onPrev={this.props.loadPrevEvents}
                  filters={threatFilters}
                  totalSize={totalSize % 21 !== 0 ? events.length : events.length + 1}
                  isLastPage={isLastPage}
                  cacheSize={events.length}
                  from={'incidentList'}
                />
              </div>
            </>
          </LoaderWrapper>
        </div>
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  events: state.entities.events.list,
  threatFilters: state.entities.events.threat.filters,
  loading: state.entities.events.loading,
  totalSize: state.entities.events.totalSize,
  isLastPage: state.entities.events.isLastPage,
  selectedCustomer: state.entities.customers.selected,
})

const mapDispatchToProps = (dispatch) => ({
  loadNextEvents: (offset, customerId, defaultCustomerId, isGlobalView) =>
    dispatch(loadNextEvents(offset, customerId, defaultCustomerId, isGlobalView)),
  loadPrevEvents: (offset, customerId, defaultCustomerId, isGlobalView) =>
    dispatch(loadPrevEvents(offset, customerId, defaultCustomerId, isGlobalView)),
  loadEvents: (filters, selectedId, defaultCustomerId, isGlobalView) =>
    dispatch(loadEvents(filters, selectedId, defaultCustomerId, isGlobalView)),
  setThreatFiltersInStore: (filters) => dispatch(setThreatFiltersInStore(filters)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ThreatFeedTable)
